import { ContentCard } from '@braze/react-native-sdk';

import {
  parseDayParts,
  parseDaysOfWeek,
  parseTimeRanges,
} from 'components/features/components/braze-marketing-card/utils';
import { IMarketingCardRules } from 'components/features/components/marketing-card-group/types';
import {
  hasRules,
  tileShouldBeDisplayed,
} from 'components/features/components/marketing-card-group/utils';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import {
  CONTENT_CARD_FEED,
  CONTENT_CARD_SETTING,
  ContentCardTypes,
  WebContentCard,
} from '../types';

export const parseWebContentCards = (webcards: WebContentCard[]): ContentCard[] => {
  return webcards
    .map(webcard => {
      const sharedProps = {
        id: webcard.id,
        url: webcard.url || '',
        image: webcard.imageUrl || '',
        created: new Date(webcard.created).getTime(),
        expiresAt: new Date(webcard.expiresAt).getTime(),
        pinned: webcard.pinned,
        dismissible: webcard.dismissible,
        dismissed: webcard.dismissed,
        clicked: webcard.clicked,
        viewed: webcard.viewed,
        isControl: webcard.isControl,
        extras: webcard.extras,
        openURLInWebView: false,
      };

      const cardTypeMap: Record<string, Partial<ContentCard>> = {
        'ab-captioned-image': {
          type: ContentCardTypes.CAPTIONED,
          title: webcard.title || '',
          cardDescription: webcard.description || '',
          domain: webcard.linkText || '',
          imageAspectRatio: webcard.aspectRatio,
        },
        'ab-banner ab-image-only': {
          type: ContentCardTypes.IMAGE_ONLY,
          imageAspectRatio: webcard.aspectRatio,
        },
        'ab-classic-card': {
          type: ContentCardTypes.CLASSIC,
          title: webcard.title || '',
          cardDescription: webcard.description || '',
          domain: webcard.linkText || '',
        },
      };

      const cardClassType = webcard.ic;
      if (!cardTypeMap[cardClassType] && !webcard.isControl) {
        return null;
      }

      return {
        ...sharedProps,
        ...cardTypeMap[cardClassType],
      } as ContentCard;
    })
    .filter((card): card is ContentCard => card !== null);
};

/**
 * Returns the array of Braze Content Cards that have been read by the user.
 * @returns an array of Read Content Cards IDs.
 */
export const getReadContentCardsFromLocalStorage = (): string[] => {
  return syncStorage.getObject<string[]>(SyncStorageKey.READ_CONTENT_CARDS) || [];
};

/**
 * Updates the Read Content Cards Array in Local Storage.
 * @param ids Array of Content Cards IDs to be marked as read.
 */
export const setReadContentCardsInLocalStorage = (ids: string[]) => {
  const readContentCards = getReadContentCardsFromLocalStorage();
  const uniqueReadContentCards = new Set([...readContentCards, ...ids]);

  syncStorage.setObject(SyncStorageKey.READ_CONTENT_CARDS, [...uniqueReadContentCards]);
};

/**
 * Compares the current (non-expired) content cards with the ones stored in Local Storage.
 * @param validContentCardIds Array of IDs of non-expired Braze content cards.
 */
export const removeExpiredContentCardsFromLocalStorage = (validContentCardIds: string[]) => {
  const readContentCards = getReadContentCardsFromLocalStorage();
  const nonExpiredContentCards = readContentCards.filter(id => validContentCardIds.includes(id));

  syncStorage.setObject(SyncStorageKey.READ_CONTENT_CARDS, nonExpiredContentCards);
};

// Cards without the feed property will be visible in both feeds. For the News feed specifically,
// we need to add this property to calculate unread messages and display the badge on the bell icon.
export const filterContentCardsByFeed = <T extends ContentCard | WebContentCard>({
  cards,
  isNewsFeedEnabled = false,
  isHomePageFeedEnabled = false,
}: {
  cards: T[];
  isNewsFeedEnabled: boolean;
  isHomePageFeedEnabled: boolean;
}): T[] => {
  let currentFeed = '';

  if (isNewsFeedEnabled) {
    currentFeed = CONTENT_CARD_FEED.NEWS;
  } else if (isHomePageFeedEnabled) {
    currentFeed = CONTENT_CARD_FEED.HOME_PAGE;
  }

  return cards
    .filter(({ extras }) => !extras.feed || extras.feed === currentFeed)
    .map(card => ({
      ...card,
      extras: {
        ...card.extras,
        ...(!card.extras.feed && currentFeed && { feed: currentFeed }),
      },
    }));
};

export const dedupeByTitleAndDescription = (contentCards: ContentCard[]): ContentCard[] => {
  const latestByKey = new Map<string, ContentCard>();
  const passthrough: ContentCard[] = [];

  for (const card of contentCards) {
    if ('title' in card && 'cardDescription' in card) {
      const title = card.title ?? '';
      const description = card.cardDescription ?? '';

      if (!title || !description) {
        passthrough.push(card);
        continue;
      }

      const key = `${title}|${description}`;
      const existing = latestByKey.get(key);

      if (!existing || card.created > existing.created) {
        latestByKey.set(key, card);
      }
    } else {
      passthrough.push(card);
    }
  }

  return [...latestByKey.values(), ...passthrough].sort((a, b) => b.created - a.created);
};

// Filter out Content Cards that are not within the allowed days of the week or time ranges.
export const filterContentCardsByRules = <T extends ContentCard | WebContentCard>(
  contentCards: T[],
  activeDayParts: IDayPartBoundary[]
): T[] => {
  return contentCards.filter(({ extras }) => {
    const rules: IMarketingCardRules = {
      daysOfWeek: parseDaysOfWeek(extras[CONTENT_CARD_SETTING.daysOfWeek]),
      timeRanges: parseTimeRanges(extras[CONTENT_CARD_SETTING.timeRanges]),
    };
    const showCardByMarketingCardRules = !hasRules(rules) || tileShouldBeDisplayed(rules);

    const cardDayParts = parseDayParts(extras[CONTENT_CARD_SETTING.dayParts]);
    const showByDayPart =
      cardDayParts && cardDayParts.length > 0
        ? cardDayParts?.some(cardDayPart =>
            activeDayParts.some(({ key }) => key.toLowerCase() === cardDayPart)
          )
        : true;

    const excludedCardDayParts = parseDayParts(extras[CONTENT_CARD_SETTING.excludedDayParts]);

    const isExcludedDayPart = excludedCardDayParts?.some(excludedDayPart =>
      activeDayParts.some(({ key }) => key.toLowerCase() === excludedDayPart)
    );

    return showCardByMarketingCardRules && showByDayPart && !isExcludedDayPart;
  });
};
